// @flow
import { HTTP_METHODS, MODULARUI_STATUS } from "../../constants/Constants";
import Href from "../../models/href/Href";
import ErrorResponse from "../../models/error/ErrorResponse";
import { finishProgress, startProgress } from "../actions/ProgressIndicator";
import { handleError } from "../actions/Error";

import type { ModularUIModel } from "../../models/types";
import type { Dispatch, ThunkAction } from "../types";
import type {
  ModularUIAction,
  SetModelAction,
  InitModelAction,
  UpdateModelAction,
  UpdateFormAction,
  RemoveModelByKeyAction,
  ResetModularUIAction,
  UpdateStatusAction,
} from "./types";
import type {
  RequestModularUIOptions,
  UpdateHandler,
} from "../../utils/fetch/types";

/**
 */
export const setModel = (
  key: string,
  model: ModularUIModel,
): SetModelAction => {
  // set key on model for later reference
  model.connectKey = key;
  return {
    type: "MODULARUI/SET",
    payload: {
      key,
      model,
    },
  };
};

/**
 */
export const initModels = (
  models: Array<{ key: string, model: ModularUIModel }>,
): InitModelAction => ({
  type: "MODULARUI/INIT",
  payload: models,
});

/**
 */
export const updateModel = (model: ModularUIModel): UpdateModelAction => ({
  type: "MODULARUI/UPDATE",
  payload: model,
});

/**
 */
export const updateForm = (model: ModularUIModel): UpdateFormAction => ({
  type: "MODULARUI/UPDATE_FORM",
  payload: model,
});

/**
 */
export const removeModelByKey = (key: string): RemoveModelByKeyAction => ({
  type: "MODULARUI/REMOVE_KEY",
  payload: key,
});

/**
 * Removes all models except the application model from the modular ui reducer
 */
export const resetModularUI = (): ResetModularUIAction => ({
  type: "MODULARUI/RESET",
});

/**
 */
export const updateStatus = (
  key: string,
  status: $Keys<typeof MODULARUI_STATUS>,
  requestOptions: any,
): UpdateStatusAction => ({
  type: "MODULARUI/STATUS",
  payload: { key, status, requestOptions },
});

/**
 */
const loadModelSuccessAction = (
  key: string,
  model: ModularUIModel,
  updateHandler: UpdateHandler | void,
): UpdateModelAction | SetModelAction => {
  if (updateHandler) {
    return updateModel(updateHandler(model));
  }
  return setModel(key, model);
};

/**
 * Retrieve the preferred language for this request, falls back to the browser language or english
 */
const getRequestLocale = (optionLocale: ?string): string => {
  if (optionLocale) {
    return optionLocale;
  }

  // $FlowIssue
  return globalThis.navigator?.language?.split("-")[0] || "en";
};

/**
 * This action is handled by the modularui middleware
 */
export const loadModel = (
  key: string,
  href: Href | string,
  options?: RequestModularUIOptions,
): ModularUIAction => ({
  type: "MODULARUI/FETCH",
  payload: {
    key,
    origin: options?.origin,
    contextPath: options?.contextPath,
    href:
      href instanceof Href
        ? href
        : new Href(href, "", {
            origin: options?.origin,
            contextPath: options?.contextPath,
          }),
    method: options?.method ?? HTTP_METHODS.GET,
    params: options?.params,
    data: options?.data,
    timeout: options?.timeout,
    headers: options?.headers,
    events: options?.events,
    onProgress: options?.onProgress,
    locale: getRequestLocale(options?.locale),
    childmodels: options?.childmodels,
    targetModel: options?.targetModel,
    forceTargetModel: options?.forceTargetModel,
    cache: options?.cache,
    isReload: options?.isReload,
    withCredentials: options?.withCredentials,
    /**
     */
    successAction: (model) =>
      loadModelSuccessAction(key, model, options?.updateHandler),
    /**
     */
    errorAction: (error) => {
      const errorResponse = new ErrorResponse(error, key, {
        origin: options?.origin,
        contextPath: options?.contextPath,
      });
      if (
        errorResponse.isChangePassword ||
        errorResponse.isResourceNotFoundAfterReload
      ) {
        return removeModelByKey(key);
      }
      return updateStatus(key, MODULARUI_STATUS.ERROR);
    },
  },
});

/**
 */
export const loadModularUI =
  (
    key: string,
    href: Href | string,
    options?: RequestModularUIOptions,
    retryCount: number = 3,
    retryDelay: number = 200,
  ): ThunkAction<> =>
  (dispatch: Dispatch, getState) => {
    const modularuiStore = getState()?.modularui;
    const currentRequest = modularuiStore?.[key];

    const isLoading = currentRequest?.status === MODULARUI_STATUS.LOADING;

    if (isLoading) {
      const isDifferentRequest =
        currentRequest.requestOptions?.href?.toString() !== href.toString();
      if (isDifferentRequest && retryCount > 0) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(
              dispatch(
                loadModularUI(key, href, options, retryCount - 1, retryDelay),
              ),
            );
          }, retryDelay);
        });
      }

      // Same request is already loading — do nothing
      return Promise.resolve();
    }

    dispatch(startProgress());

    const loadModelPromise = dispatch(loadModel(key, href, options));

    return Promise.resolve(loadModelPromise)
      .then(() => dispatch(finishProgress()))
      .catch((error) => dispatch(handleError(error)));
  };

/**
 */
export const reloadModel = (
  model: ModularUIModel,
  options?: RequestModularUIOptions,
): ThunkAction<> =>
  loadModularUI(model.connectKey, model.selfhref, {
    ...options,
    isReload: true,
  });
